import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '@/auth/AuthContext'
import { useProjectStore, type WorkbenchTab } from '@/store/projectStore'
import { useDockUiStore } from '@/store/dockUiStore'
import { prefillDock, useDockBridgeStore } from '@/store/dockBridgeStore'
import { refreshJourney, useJourneyStore } from '@/store/journeyStore'
import { loadSampleData, testScreens, type JourneyTab } from '@/api/journey'

/**
 * The journey's CLOSED action vocabulary (backend data/journeys/cards.json):
 *
 *   tab:<id>                     open a Workbench tab
 *   agent:continue|build_rest|pause|show   the dock's own build controls
 *   open:settings|people_invite|sample_data|golive|add_screen|domains|
 *        spam_protection|schedulers|publish|run
 *   ask:<prompt>                 pre-fill the Ask-AI box (never sends)
 *   copy:<text>                  copy to the clipboard
 *   test:all | test:<type>:<name>  check screens with the Testing tool (real browser)
 *
 * One runner for the Next step card, the strip, the Go live checklist and the
 * Plan tab, so a card's button can never mean two different things.
 */

/** Prompt used when there is no attached build session to continue. */
const CONTINUE_FALLBACK = 'Continue building the app: build every screen that is not built yet.'
const SAMPLE_FALLBACK = 'Create sample data for every screen of the app, so each one has a few realistic records to try.'

export function runUrl(tenant: string | undefined | null, folder: string, objName?: string): string {
  const base = `/run/${encodeURIComponent(tenant || '_')}/${encodeURIComponent(folder)}`
  return objName ? `${base}/${encodeURIComponent(objName)}` : base
}

export function useJourneyAction() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const setPanelOpen = useDockUiStore((s) => s.setPanelOpen)

  return useCallback(async (action: string | null | undefined) => {
    if (!action) return
    const project = useProjectStore.getState().currentProject?.application.folder_name
    const [kind, ...rest] = action.split(':')
    const arg = rest.join(':')
    const goTab = (tab: string, search = '') => {
      if (!project) return
      const go = () => {
        // `advanced/support` = the Advanced tab, its Support tool.
        useProjectStore.getState().setActiveTab(tab.split('/')[0] as WorkbenchTab)
        navigate(`/workbench/${encodeURIComponent(project)}/${tab}${search}`)
      }
      const guard = useProjectStore.getState().prototypeLeaveGuard
      if (guard) guard(go)
      else go()
    }

    try {
      switch (kind) {
        case 'tab':
          goTab(arg)
          return
        case 'ask':
          prefillDock(arg)
          setPanelOpen(true)
          return
        case 'test': {
          if (!project) return
          // test:all = every built screen not yet checked; test:T:orders = one.
          const [t, ...nameParts] = arg.split(':')
          const objects: Array<[string, string]> = arg === 'all' || !nameParts.length ? [] : [[t, nameParts.join(':')]]
          try {
            const r = await testScreens(project, objects)
            toast.success(objects.length
              ? 'Testing it now in a browser — the result appears here'
              : `Testing ${r.objects.length} screen${r.objects.length === 1 ? '' : 's'} now — results appear as they finish`)
            if (r.untested.length) toast(`No automatic test for: ${r.untested.slice(0, 5).join(', ')} — check those yourself`, { duration: 7000 })
          } catch (err) {
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
            toast.error(typeof detail === 'string' && detail ? detail : 'The test could not start')
          }
          refreshJourney()
          return
        }
        case 'copy':
          try {
            await navigator.clipboard.writeText(arg.startsWith('/') ? window.location.origin + arg : arg)
            toast.success('Link copied')
          } catch {
            toast(arg, { duration: 8000 })
          }
          return
        case 'agent': {
          const bridge = useDockBridgeStore.getState()
          const live = bridge.project === project ? bridge.actions : null
          setPanelOpen(true)
          if (arg === 'show') return
          if (arg === 'pause') {
            if (live) live.pause()
            else toast('Nothing is building right now')
            return
          }
          if (live && bridge.hasQuestion) {
            // The build is stopped on a question — the panel (opened above)
            // shows it; continuing would only show it again.
            toast('The agent is waiting for your answer — it is in the twasta.ai agent panel')
            return
          }
          if (live && bridge.agentStatus === 'running' && !bridge.canContinue) {
            toast('It is already building — watch it in the twasta.ai agent panel')
            return
          }
          if (live) {
            if (arg === 'build_rest') live.completeEverything()
            else live.continueNextPhase()
          } else {
            // No build session attached to the dock (a hand-built project, or a
            // dock on another engine): ask for the same thing in words.
            prefillDock(CONTINUE_FALLBACK)
          }
          window.setTimeout(refreshJourney, 1500)
          return
        }
        case 'open':
          switch (arg) {
            case 'settings': goTab('settings'); return
            case 'golive': goTab('golive'); return
            case 'people_invite': goTab(peopleTab(), '?invite=1'); return
            case 'add_screen': goTab(objectsTab(), '?add=1'); return
            case 'domains': if (project) navigate(`/domains/${encodeURIComponent(project)}`); return
            case 'spam_protection': if (project) navigate(`/spam-protection/${encodeURIComponent(project)}`); return
            case 'schedulers': if (project) navigate(`/designers/scheduler/${encodeURIComponent(project)}`); return
            case 'publish': if (project) navigate(`/deploy/${encodeURIComponent(project)}`); return
            case 'run':
              if (project) window.open(runUrl(user?.tenant_id, project), '_blank', 'noopener')
              return
            case 'sample_data': {
              if (!project) return
              const t = toast.loading('Loading sample data…')
              try {
                const rep = await loadSampleData(project)
                toast.dismiss(t)
                if (rep.failed) toast.error(`${rep.created} added, ${rep.failed} could not be saved`)
                else toast.success(rep.created ? `${rep.created} sample records added` : 'Sample data is already there')
                refreshJourney()
              } catch (err) {
                toast.dismiss(t)
                const code = (err as { response?: { data?: { detail?: { error?: { code?: string } } } } })
                  ?.response?.data?.detail?.error?.code
                console.warn('[journey] sample data load failed', err)
                // No seed file yet — ask the AI to make one.
                prefillDock(SAMPLE_FALLBACK)
                setPanelOpen(true)
                if (code !== 'SEED_INVALID') toast.error('Sample data could not be loaded — ask the AI to create it')
              }
              return
            }
            default:
              console.warn('[journey] unknown open action', arg)
              return
          }
        default:
          console.warn('[journey] unknown action', action)
      }
    } catch (err) {
      console.error('[journey] action failed', action, err)
      toast.error('That did not work — please try again')
    }
  }, [navigate, user?.tenant_id, setPanelOpen])
}

/** The open project's objects tab (Screens / Pages / Dashboards…). */
function objectsTab(): string {
  return firstTab((t) => t.view === 'objects') || 'screens'
}

function peopleTab(): string {
  return firstTab((t) => t.view === 'people') || 'people'
}

function firstTab(pred: (t: JourneyTab) => boolean): string | null {
  const tabs = useJourneyStore.getState().journey?.tabs || []
  return tabs.find(pred)?.id || null
}
